import type { EixoColuna } from '../geometria/anguloExtracao'
import { girarEmTorno, somar, vetor, type Vetor3 } from '../geometria/vetor3'
import type { AncoraOcupante } from './perfilVeiculo'
import type { ProporcoesVitima } from './perfisVitima'

/**
 * Pose do manequim articulado, em radianos.
 *
 * O rig é intencionalmente pobre: cada articulação tem um grau de liberdade
 * relevante para a didática. Isso mantém a pose serializável, testável e
 * independente de Three.js — o adaptador visual só lê estes ângulos.
 *
 * Convenção: inclinação positiva = para frente (peito em direção a +Z local).
 */
export interface PoseVitima {
  pelveTorcaoRad: number
  troncoInclinacaoRad: number
  troncoTorcaoRad: number
  troncoLateralRad: number
  cabecaInclinacaoRad: number
  cabecaTorcaoRad: number
  /** quadril: 0 = coxa alinhada ao tronco; negativo = coxa levantada à frente */
  quadrilRad: number
  /** abdução das coxas, afasta os joelhos */
  abducaoCoxaRad: number
  /**
   * joelho, relativo à coxa. Como o ângulo é acumulativo, sentar exige o
   * simétrico do quadril: coxa à frente (quadril negativo) e canela de volta
   * para baixo (joelho positivo).
   */
  joelhoRad: number
  ombroRad: number
  cotoveloRad: number
}

/**
 * Pose sentada de referência.
 *
 * `troncoInclinacaoRad` é ZERO de propósito: com a coluna vertical o eixo
 * anatômico coincide com +Y, as rotas de extração ficam horizontais e o
 * ângulo devolvido pelo domínio é exatamente o nominal (0/30/60/90).
 * O encosto do banco recebe a inclinação visual, não o eixo de cálculo.
 */
export const POSE_SENTADA: PoseVitima = {
  pelveTorcaoRad: 0,
  troncoInclinacaoRad: 0,
  troncoTorcaoRad: 0,
  troncoLateralRad: 0,
  cabecaInclinacaoRad: 0.05,
  cabecaTorcaoRad: 0,
  quadrilRad: -1.48,
  abducaoCoxaRad: 0.16,
  joelhoRad: 1.42,
  ombroRad: -0.85,
  cotoveloRad: -0.35,
}

/** Pose em pé/deitada usada no fim da rota de extração (corpo alinhado à maca). */
export const POSE_ALINHADA: PoseVitima = {
  ...POSE_SENTADA,
  quadrilRad: -0.2,
  joelhoRad: 0.18,
  abducaoCoxaRad: 0.05,
  ombroRad: -0.3,
  cotoveloRad: -0.2,
}

export function misturarPose(a: PoseVitima, b: PoseVitima, t: number): PoseVitima {
  const f = (x: number, y: number) => x + (y - x) * t
  return {
    pelveTorcaoRad: f(a.pelveTorcaoRad, b.pelveTorcaoRad),
    troncoInclinacaoRad: f(a.troncoInclinacaoRad, b.troncoInclinacaoRad),
    troncoTorcaoRad: f(a.troncoTorcaoRad, b.troncoTorcaoRad),
    troncoLateralRad: f(a.troncoLateralRad, b.troncoLateralRad),
    cabecaInclinacaoRad: f(a.cabecaInclinacaoRad, b.cabecaInclinacaoRad),
    cabecaTorcaoRad: f(a.cabecaTorcaoRad, b.cabecaTorcaoRad),
    quadrilRad: f(a.quadrilRad, b.quadrilRad),
    abducaoCoxaRad: f(a.abducaoCoxaRad, b.abducaoCoxaRad),
    joelhoRad: f(a.joelhoRad, b.joelhoRad),
    ombroRad: f(a.ombroRad, b.ombroRad),
    cotoveloRad: f(a.cotoveloRad, b.cotoveloRad),
  }
}

/** Colocação do manequim no mundo: âncora + deslocamento e rotação de extração. */
export interface ColocacaoVitima {
  /** centro da pelve, em coordenadas de mundo */
  posicaoPelve: Vetor3
  /** rotação do corpo inteiro em torno de +Y (guinada) */
  guinadaRad: number
  /**
   * Reclinação do corpo inteiro para trás, em torno do eixo lateral da vítima.
   * 0 = sentado, ~π/2 = deitado de costas sobre a prancha.
   *
   * É o movimento da manobra, não do cálculo: o ângulo de retirada é medido na
   * posição sentada inicial, antes de deitar.
   */
  reclinacaoRad?: number
  /**
   * Rolagem do corpo inteiro em torno do próprio eixo longitudinal.
   * Usada na vítima em decúbito no veículo tombado.
   */
  rolagemRad?: number
}

export function colocacaoNaAncora(ancora: AncoraOcupante): ColocacaoVitima {
  return { posicaoPelve: ancora.posicao, guinadaRad: ancora.rotacao.y, reclinacaoRad: 0 }
}

const EIXO_VERTICAL = vetor(0, 1, 0)
const EIXO_LATERAL = vetor(1, 0, 0)
const FRENTE_LOCAL = vetor(0, 0, 1)

export interface DirecoesVitima {
  /** eixo da coluna, da base para o topo, normalizado */
  eixo: Vetor3
  /** para onde a vítima olha, normalizado */
  frente: Vetor3
}

/**
 * Eixo e frente do corpo, já com pose, guinada e reclinação aplicadas.
 *
 * Separado de `eixoColunaDaVitima` porque três coisas precisam das MESMAS
 * direções: o eixo da coluna, o deslocamento da pelve até a base da coluna e a
 * posição da cabeça. Recalcular cada uma por conta própria é como a cabeça
 * acabava desenhada fora do corpo com a vítima deitada.
 */
export function direcoesDaVitima(
  colocacao: ColocacaoVitima,
  pose: PoseVitima = POSE_SENTADA,
  inclinacaoExtra = 0,
): DirecoesVitima {
  const guinadaTotal = colocacao.guinadaRad + pose.pelveTorcaoRad

  // direção da coluna: vertical, inclinada para frente, torcida lateralmente e
  // finalmente girada pela guinada do corpo.
  let eixo = girarEmTorno(EIXO_VERTICAL, EIXO_LATERAL, pose.troncoInclinacaoRad + inclinacaoExtra)
  eixo = girarEmTorno(eixo, vetor(0, 0, 1), pose.troncoLateralRad)
  eixo = girarEmTorno(eixo, EIXO_VERTICAL, guinadaTotal + pose.troncoTorcaoRad)

  let frente = girarEmTorno(FRENTE_LOCAL, EIXO_LATERAL, pose.troncoInclinacaoRad + inclinacaoExtra)
  frente = girarEmTorno(frente, vetor(0, 0, 1), pose.troncoLateralRad)
  frente = girarEmTorno(frente, EIXO_VERTICAL, guinadaTotal + pose.troncoTorcaoRad)

  // deitar o corpo gira tudo em torno do eixo lateral da própria vítima
  const reclinacao = colocacao.reclinacaoRad ?? 0
  if (Math.abs(reclinacao) > 1e-9) {
    const lateral = girarEmTorno(EIXO_LATERAL, EIXO_VERTICAL, guinadaTotal)
    eixo = girarEmTorno(eixo, lateral, -reclinacao)
    frente = girarEmTorno(frente, lateral, -reclinacao)
  }

  const rolagem = colocacao.rolagemRad ?? 0
  if (Math.abs(rolagem) > 1e-9) {
    frente = girarEmTorno(frente, eixo, rolagem)
  }

  return { eixo, frente }
}

const aoLongo = (v: Vetor3, escala: number): Vetor3 =>
  vetor(v.x * escala, v.y * escala, v.z * escala)

/**
 * Eixo da coluna da vítima em coordenadas de mundo, derivado da pose e da
 * colocação. É esta função — e não coordenadas escritas na UI — que alimenta
 * `calcularAnguloExtracao`, para que o desenho e a matemática concordem.
 */
export function eixoColunaDaVitima(
  colocacao: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  pose: PoseVitima = POSE_SENTADA,
): EixoColuna {
  const { eixo, frente } = direcoesDaVitima(colocacao, pose)

  if (proporcoes.alturaTronco <= 0) {
    throw new Error('alturaTronco precisa ser positiva: sem tronco não existe eixo da coluna.')
  }

  // do centro da pelve até a base da coluna o deslocamento é AO LONGO DO CORPO,
  // não para cima no mundo. Sentada dá no mesmo (o eixo é +Y); deitada no teto
  // do veículo capotado, somar em +Y levantava a coluna 9 cm acima do corpo.
  const base = somar(colocacao.posicaoPelve, aoLongo(eixo, proporcoes.alturaPelve / 2))
  const topo = somar(base, aoLongo(eixo, proporcoes.alturaTronco))

  return { baseColuna: base, topoColuna: topo, frenteVitima: frente }
}

/**
 * Centro de cada ombro, em coordenadas de mundo.
 *
 * É a linha dos ombros que varre o arco quando o corpo gira: a cabeça fica
 * praticamente SOBRE o eixo de rotação da vítima sentada — `POSE_SENTADA` tem
 * `troncoInclinacaoRad` zero de propósito — e mal se desloca com a guinada.
 * Os ombros também já são a referência de folga do projeto (`obstaculosRota.ts`),
 * então medir o giro por eles mantém uma referência só.
 */
export function posicaoOmbros(
  colocacao: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  pose: PoseVitima = POSE_SENTADA,
): { esquerdo: Vetor3; direito: Vetor3 } {
  const { topoColuna } = eixoColunaDaVitima(colocacao, proporcoes, pose)
  const { eixo, frente } = direcoesDaVitima(colocacao, pose)
  // com o eixo em +Y e a frente em +Z, este produto vetorial aponta para +X —
  // o lado DIREITO da vítima, coerente com `LadoRotacao`
  const direita = vetor(
    eixo.y * frente.z - eixo.z * frente.y,
    eixo.z * frente.x - eixo.x * frente.z,
    eixo.x * frente.y - eixo.y * frente.x,
  )
  const meia = proporcoes.larguraOmbros / 2
  return {
    esquerdo: somar(topoColuna, aoLongo(direita, -meia)),
    direito: somar(topoColuna, aoLongo(direita, meia)),
  }
}

/** Ponto aproximado do centro da cabeça, usado para desenhar rastros. */
export function posicaoCabeca(
  colocacao: ColocacaoVitima,
  proporcoes: ProporcoesVitima,
  pose: PoseVitima = POSE_SENTADA,
): Vetor3 {
  const { topoColuna } = eixoColunaDaVitima(colocacao, proporcoes, pose)
  const alturaCabeca = proporcoes.alturaPescoco + proporcoes.raioCabeca
  // a cabeça segue o eixo do corpo mais a própria inclinação dela: com a vítima
  // deitada, um eixo vertical fixo punha a cabeça acima do tronco em vez de à frente
  const { eixo } = direcoesDaVitima(colocacao, pose, pose.cabecaInclinacaoRad)
  return somar(topoColuna, aoLongo(eixo, alturaCabeca))
}
